import React, { useState } from "react";
import { submitLead, errMessage } from "@/lib/api";
import { PRODUCTS } from "@/data/site";
import { Loader2, Check, ArrowLeft, ArrowRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";

const steps = ["Loan", "You", "Contact"];

export default function Apply() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ loan_product: PRODUCTS[0].name, loan_amount: 1500000, name: "", mobile: "", email: "", city: "", message: "" });
  const [busy, setBusy] = useState(false); const [result, setResult] = useState(null); const [err, setErr] = useState("");
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'number' ? Number(e.target.value) : e.target.value });
  const canNext = () => (step === 0 ? form.loan_product && form.loan_amount > 0 : step === 1 ? form.name.trim().length > 1 : /^\d{10}$/.test(form.mobile));

  async function submit() {
    setErr(""); setBusy(true);
    try {
      const payload = { ...form, email: form.email?.trim() ? form.email.trim() : undefined };
      setResult(await submitLead(payload));
    }
    catch (e) { setErr(errMessage(e)); }
    finally { setBusy(false); }
  }

  return (
    <main data-testid="apply-page" className="pt-24 md:pt-32 pb-24">
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="chapter-index text-emerald-700">Apply · 3 quick steps</p>
        <h1 className="font-display text-5xl md:text-7xl leading-[0.95] mt-3">Let's get you <span className="italic text-emerald-800">financed.</span></h1>

        {!result && (
          <div className="mt-10 rounded-3xl bg-white border border-black/[0.06] p-8 md:p-10">
            <div className="flex items-center gap-4"><Progress value={((step+1)/steps.length)*100} className="h-1"/><p className="text-sm text-slate-500 font-mono">{step+1}/{steps.length}</p></div>
            <p className="mt-6 chapter-index text-slate-500">Step · {steps[step]}</p>

            {step === 0 && (
              <div className="mt-6 grid gap-4">
                <label><span className="text-xs uppercase tracking-widest text-slate-500">Loan product</span>
                  <select data-testid="apply-product" value={form.loan_product} onChange={on('loan_product')} className="input mt-2">{PRODUCTS.map(p=><option key={p.slug}>{p.name}</option>)}</select>
                </label>
                <label><span className="text-xs uppercase tracking-widest text-slate-500">Loan amount (₹)</span>
                  <input data-testid="apply-amount" type="number" value={form.loan_amount} onChange={on('loan_amount')} className="input mt-2"/>
                </label>
                <label><span className="text-xs uppercase tracking-widest text-slate-500">Notes (optional)</span>
                  <textarea data-testid="apply-message" value={form.message} onChange={on('message')} rows={3} placeholder="Tell us anything specific about your requirement." className="input mt-2"/>
                </label>
              </div>
            )}

            {step === 1 && (
              <div className="mt-6 grid md:grid-cols-2 gap-4">
                <label className="md:col-span-2"><span className="text-xs uppercase tracking-widest text-slate-500">Full name</span>
                  <input required data-testid="apply-name" value={form.name} onChange={on('name')} className="input mt-2"/></label>
                <label><span className="text-xs uppercase tracking-widest text-slate-500">City</span>
                  <input data-testid="apply-city" value={form.city} onChange={on('city')} className="input mt-2"/></label>
                <label><span className="text-xs uppercase tracking-widest text-slate-500">Email (optional)</span>
                  <input data-testid="apply-email" value={form.email} onChange={on('email')} type="email" className="input mt-2"/></label>
              </div>
            )}

            {step === 2 && (
              <div className="mt-6 grid gap-4">
                <label><span className="text-xs uppercase tracking-widest text-slate-500">10-digit mobile</span>
                  <input required data-testid="apply-mobile" value={form.mobile} onChange={on('mobile')} inputMode="numeric" pattern="\d{10}" className="input mt-2"/></label>
                <p className="text-[11px] text-slate-500">By submitting, you consent to be contacted about your enquiry. Loan approval is subject to lender policies. Rightway Finance does not guarantee approval.</p>
              </div>
            )}

            {err && <p data-testid="apply-error" className="mt-3 text-sm text-red-600">{err}</p>}

            <div className="mt-8 flex items-center justify-between">
              <button data-testid="apply-back" disabled={step===0} onClick={()=>setStep(step-1)} className="inline-flex items-center gap-2 text-sm text-slate-700 disabled:opacity-30"><ArrowLeft size={14}/> Back</button>
              {step < 2 ? (
                <button data-testid="apply-next" disabled={!canNext()} onClick={()=>setStep(step+1)} className="btn-line inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-5 py-2.5 text-sm disabled:opacity-40">Next <ArrowRight size={14}/></button>
              ) : (
                <button data-testid="apply-submit" onClick={submit} disabled={busy || !canNext()} className="btn-line inline-flex items-center gap-2 rounded-full bg-emerald-700 text-white px-5 py-2.5 text-sm disabled:opacity-40">
                  {busy ? <Loader2 className="animate-spin" size={14}/> : <>Submit Application <ArrowRight size={14}/></>}
                </button>
              )}
            </div>
          </div>
        )}

        {result && (
          <div data-testid="apply-success" className="mt-10 rounded-3xl bg-emerald-50 border border-emerald-200 p-8 md:p-10">
            <div className="w-10 h-10 rounded-full bg-emerald-600 grid place-items-center text-white"><Check size={20}/></div>
            <p className="mt-4 font-display text-3xl">Application received.</p>
            <p className="mt-2 text-slate-600">Your application ID:</p>
            <p data-testid="apply-application-id" className="mt-1 font-mono text-lg text-emerald-800">{result.application_id}</p>
            <p className="mt-6 text-sm text-slate-600">Save this ID to check status. Our advisor will call within 24 hours.</p>
            <div className="mt-6 flex gap-3">
              <a href={`/track`} className="btn-line rounded-full bg-slate-900 text-white px-6 py-3 text-sm inline-flex items-center gap-2">Track your application <ArrowRight size={14}/></a>
            </div>
          </div>
        )}
        <style>{`.input{width:100%;border:1px solid hsl(214 32% 91%);background:#fff;border-radius:1rem;padding:.75rem 1rem;font-size:.9rem;outline:none}.input:focus{border-color:hsl(158 64% 40%)}`}</style>
      </section>
    </main>
  );
}
